import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

const FileConverterNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const from = data.config?.from || 'docx';
  const to = data.config?.to || 'pdf';

  const getFormatIcon = (format: string) => {
    const icons: Record<string, string> = {
      pdf: '📕',
      docx: '📘',
      doc: '📘',
      xlsx: '📗',
      xls: '📗',
      pptx: '📙',
      ppt: '📙',
      txt: '📄',
      html: '🌐',
      md: '📝',
      jpg: '🖼️',
      png: '🖼️',
      gif: '🖼️'
    };
    return icons[format.toLowerCase()] || '📄';
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-pink-400 to-pink-500 border-2 transition-all duration-200 ${
      selected ? 'border-pink-600 shadow-xl scale-105' : 'border-pink-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ArrowsRightLeftIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-pink-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Conversion display */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm">
              {getFormatIcon(from)}
            </span>
            <span className="text-xs text-pink-100 font-semibold">
              {from.toUpperCase()}
            </span>
            <span className="text-xs text-pink-100">
              →
            </span>
            <span className="text-sm">
              {getFormatIcon(to)}
            </span>
            <span className="text-xs text-pink-100 font-semibold">
              {to.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData?.converted_file && (
        <div className="mt-2 text-xs text-pink-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Konvertálva:</strong> ✅ Sikeres
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-pink-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-pink-600 border-2 border-white"
      />
    </div>
  );
};

export default FileConverterNode;
